import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { KPIService } from '../../api/kpi/KPIService';
import KpiIndicatorCard from '../../components/Dashboard/KpiIndicatorCard';
import DateRangeSearchBar from '../../components/Search/DateRangeSearchBar';
import PageShell from '../../components/Layout/PageShell';
import { matchCalcCode } from '../../utils/kpiCalcCodeDescription';
import './KPI.css';

/**
 * 트랙 R2b — `/kpi` 페이지 wrapper 책임을 PageShell 로 위임.
 * 본인 회사 등록 KPI 지표를 카드 그리드로 종합 표시.
 * 모듈 조립만 수행: KpiIndicatorCard + DateRangeSearchBar + matchCalcCode util + PageShell.
 * 차트 라이브러리 / 기간 picker / 대시보드 차트 / react-icons 직접 import 금지 (AST 가드).
 */
const KPI = () => {
  const session = useSelector((state) => state.session?.data) || {};
  const companySeq = session.companySeq;
  const companyName = session.companyName;

  const [indicators, setIndicators] = useState([]);
  const [period, setPeriod] = useState({ start: '', end: '' });
  const [trendMap, setTrendMap] = useState({}); // { 'fld_dtl': [{date,value},...] }
  const [loading, setLoading] = useState(false);

  // 1) 마운트 / companySeq 변경 시 indicators fetch (멀티테넌트 가드)
  useEffect(() => {
    if (!companySeq) return;
    KPIService.indicators({ companySeq })
      .then((list) => setIndicators(Array.isArray(list) ? list : []))
      .catch(() => setIndicators([]));
  }, [companySeq]);

  // 2) DateRangeSearchBar onSearch — 기간만 갱신 (실제 fetch 는 useEffect 에서)
  const handleSearch = useCallback(({ start, end }) => {
    setPeriod({ start, end });
  }, []);

  // 3) (indicators, period) 변경 시 각 indicator 별 external 재호출
  useEffect(() => {
    if (!companySeq) return;
    if (!indicators || indicators.length === 0) return;
    if (!period.start || !period.end) return;
    let canceled = false;
    setLoading(true);
    Promise.all(
      indicators.map(async (ind) => {
        const key = `${ind.kpiFldCd}_${ind.kpiDtlCd}`;
        try {
          const res = await KPIService.TimeSeries.externalData({
            companySeq,
            companyName,
            fldCd: ind.kpiFldCd,
            dtlCd: ind.kpiDtlCd,
            start: period.start,
            end: period.end,
          });
          return [key, Array.isArray(res) ? res : []];
        } catch {
          return [key, []];
        }
      }),
    )
      .then((entries) => {
        if (!canceled) setTrendMap(Object.fromEntries(entries));
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [companySeq, companyName, indicators, period.start, period.end]);

  // 3) 카드 props 계산 (현재값 = 마지막, 이전값 = 직전)
  const cardPropsFor = (ind) => {
    const key = `${ind.kpiFldCd}_${ind.kpiDtlCd}`;
    const trend = trendMap[key] || [];
    const lastIdx = trend.length - 1;
    const currentValue = lastIdx >= 0 ? Number(trend[lastIdx]?.value ?? null) : null;
    const previousValue =
      lastIdx >= 1 ? Number(trend[lastIdx - 1]?.value ?? null) : null;
    const matched = matchCalcCode(ind.kpiFldCd, ind.kpiDtlCd, ind.indicatorName);
    const formula = matched ? matched.desc : null;
    return {
      indicator: ind,
      currentValue: Number.isFinite(currentValue) ? currentValue : null,
      previousValue: Number.isFinite(previousValue) ? previousValue : null,
      formula,
      trend,
      loading,
    };
  };

  return (
    <PageShell
      title="KPI"
      controls={
        <DateRangeSearchBar
          onSearch={handleSearch}
          defaultDays={30}
          loading={loading}
        />
      }
    >
      <div className="kpi-content">
        {indicators.length === 0 ? (
          <div className="no-data-indicator">등록된 KPI 지표가 없습니다.</div>
        ) : (
          <div className="kpi-grid">
            {indicators.map((ind) => (
              <KpiIndicatorCard
                key={`${ind.kpiFldCd}_${ind.kpiDtlCd}`}
                {...cardPropsFor(ind)}
              />
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default KPI;
